import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { formatDate } from '../utils/formatDate';

const ESTADOS = [
  { value: 'en_investigacion', label: 'En investigación', color: 'primary', hex: '#0d6efd' },
  { value: 'en_juicio', label: 'En juicio', color: 'warning', hex: '#ffc107' },
  { value: 'sentenciado', label: 'Sentenciado', color: 'danger', hex: '#dc3545' },
  { value: 'absuelto', label: 'Absuelto', color: 'success', hex: '#198754' },
];

const GET_ACUSADOS = gql`
  query GetAcusados {
    acusados {
      id
      nombre_completo
      cedula
      grado
      unidad
      estado_actual
      resumen_caso
      direccion
      estado_civil
      edad
      fecha_nacimiento
      foto
      created_at
      updated_at
    }
  }
`;

const UPDATE_ESTADO = gql`
  mutation UpdateEstado($id: uuid!, $estado: estado_procesal_enum!) {
    update_acusados_by_pk(pk_columns: { id: $id }, _set: { estado_actual: $estado }) {
      id
      estado_actual
      updated_at
    }
  }
`;

const mapAcusado = (row) => {
  const fecha = row.updated_at || row.created_at || '';
  return {
    acusado_id: row.id,
    nombre: row.nombre_completo,
    cedula: row.cedula,
    grado: row.grado,
    unidad: row.unidad,
    estado_procesal: row.estado_actual,
    resumen_caso: row.resumen_caso,
    direccion: row.direccion,
    estado_civil: row.estado_civil,
    edad: row.edad,
    fecha_nacimiento: row.fecha_nacimiento,
    foto: row.foto,
    fecha_actualizacion: formatDate(fecha),
  };
};

export default function KanbanAcusados({ acusados: acusadosProp, onAbrirDetalle, onActualizarAcusado, onIrRegistrar, onSetAcusados }) {
  const [acusados, setAcusados] = useState(acusadosProp || []);
  const [cargando, setCargando] = useState(!acusadosProp || acusadosProp.length === 0);
  const [busqueda, setBusqueda] = useState('');
  const [dragItem, setDragItem] = useState(null);
  const [overEstado, setOverEstado] = useState('');
  const { data, loading, error } = useQuery(GET_ACUSADOS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const [updateEstado] = useMutation(UPDATE_ESTADO);

  const dataAcusados = useMemo(() => {
    if (!data?.acusados) return null;
    return data.acusados.map(mapAcusado);
  }, [data]);

  useEffect(() => {
    if (dataAcusados && dataAcusados.length > 0) {
      setAcusados(dataAcusados);
      if (typeof onSetAcusados === 'function') {
        onSetAcusados(dataAcusados);
      }
      setCargando(false);
      return;
    }
    if (error) {
      setCargando(false);
      return;
    }
    if (loading) return;
    if (acusadosProp && acusadosProp.length > 0) {
      setAcusados(acusadosProp);
      if (typeof onSetAcusados === 'function') {
        onSetAcusados(acusadosProp);
      }
    }
    setCargando(false);
  }, [acusadosProp, dataAcusados, error, loading]);

  if (cargando) return <div>Cargando...</div>;

  const normalizar = (s = '') => (s || '').toString().toLowerCase();
  const coincide = (a) => {
    if (!busqueda) return true;
    const q = normalizar(busqueda);
    return (
      normalizar(a.nombre).includes(q) ||
      normalizar(a.cedula).includes(q) ||
      normalizar(a.unidad).includes(q) ||
      normalizar(a.grado).includes(q)
    );
  };

  const Avatar = ({ a }) => {
    const size = 28;
    const styleImg = { width: size, height: size, objectFit: 'cover', borderRadius: '50%', border: '1px solid #ddd' };
    if (a.foto) return <img src={a.foto} alt={a.nombre} style={styleImg} />;
    const iniciales = (a.nombre || '').trim().split(/\s+/).slice(0,2).map(x => x[0]?.toUpperCase() || '').join('');
    return (
      <div style={{...styleImg, display:'inline-flex', alignItems:'center', justifyContent:'center', background:'#f1f3f5', fontSize:12 }}>
        {iniciales || 'A'}
      </div>
    );
  };

  const handleDropOnEstado = async (estadoDestino) => {
    setOverEstado('');
    if (!dragItem) return;
    const a = acusados.find(x => x.acusado_id === dragItem);
    if (!a || a.estado_procesal === estadoDestino) return;
    // Optimistic update local state
    setAcusados(prev => prev.map(x => x.acusado_id === a.acusado_id ? { ...x, estado_procesal: estadoDestino } : x));
    try {
      const res = await updateEstado({ variables: { id: a.acusado_id, estado: estadoDestino } });
      if (res.data?.update_acusados_by_pk) {
        const actualizado = {
          ...a,
          estado_procesal: res.data.update_acusados_by_pk.estado_actual,
          fecha_actualizacion: res.data.update_acusados_by_pk.updated_at
            ? formatDate(res.data.update_acusados_by_pk.updated_at)
            : a.fecha_actualizacion,
        };
        onActualizarAcusado && onActualizarAcusado(actualizado);
      }
    } catch (e) {
      setAcusados(prev => prev.map(x => x.acusado_id === a.acusado_id ? { ...x, estado_procesal: a.estado_procesal } : x));
    }
    setDragItem(null);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
        <h2 className="mb-0">Tablero de Estado de Acusados</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary d-flex align-items-center gap-2" onClick={onIrRegistrar}>
            <i className="bi bi-person-plus-fill"></i>
            <span>Nuevo Acusado</span>
          </button>
          <div className="input-group" style={{ maxWidth: 380 }}>
            <span className="input-group-text">Buscar</span>
            <input className="form-control" placeholder="Nombre, cédula, unidad o grado" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="row g-3">
        {ESTADOS.map(estado => (
          <div className="col-12 col-md-6 col-lg-3" key={estado.value}>
            <div className={`card h-100 ${overEstado===estado.value ? 'bg-light' : ''}`}
                 onDragOver={(e) => { e.preventDefault(); setOverEstado(estado.value); }}
                 onDragLeave={() => setOverEstado('')}
                 onDrop={() => handleDropOnEstado(estado.value)}
            >
              <div className={`card-header text-white text-center`} style={{ backgroundColor: estado.hex }}>
                {estado.label}
              </div>
              <div className="card-body p-2" style={{ minHeight: 180, borderTop: `3px solid ${estado.hex}` }}>
                {acusados.filter(a => a.estado_procesal === estado.value && coincide(a)).length === 0 && (
                  <div className="text-muted text-center">Sin acusados</div>
                )}
                {acusados.filter(a => a.estado_procesal === estado.value && coincide(a)).map(a => (
                  <div className="card mb-2 shadow-sm" key={a.acusado_id}
                       draggable
                       onDragStart={() => setDragItem(a.acusado_id)}
                       onDragEnd={() => setDragItem(null)}
                       onClick={() => onAbrirDetalle && onAbrirDetalle(a)}
                  >
                    <div className="card-body p-2 d-flex gap-2 align-items-start" style={{ cursor: 'pointer', borderLeft: `3px solid ${estado.hex}` }}>
                      <Avatar a={a} />
                      <div>
                        <div className="fw-bold">{a.nombre}</div>
                        <div className="small text-muted">{a.grado} - {a.unidad}</div>
                        {a.cedula && <div className="small">Cédula: {a.cedula}</div>}
                        <div className="small">Actualizado: {a.fecha_actualizacion}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
